import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  type DarkVariant,
  type Theme,
  applyTheme,
  getStoredSystemDark,
  getStoredTheme,
  setStoredSystemDark,
  setStoredTheme,
} from '~/lib/theme'

type ThemeContextValue = {
  theme: Theme
  setTheme: (t: Theme) => void
  systemDark: DarkVariant
  setSystemDark: (v: DarkVariant) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [theme, setTheme] = useState<Theme>(getStoredTheme)
  const [systemDark, setSystemDark] = useState<DarkVariant>(getStoredSystemDark)

  const updateTheme = useCallback((t: Theme) => {
    setTheme(t)
    setStoredTheme(t)
    applyTheme(t, systemDark)
  }, [systemDark])

  const updateSystemDark = useCallback((v: DarkVariant) => {
    setSystemDark(v)
    setStoredSystemDark(v)
    applyTheme(theme, v)
  }, [theme])

  useEffect(() => {
    applyTheme(theme, systemDark)
    const mq = globalThis.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => { if (theme === 'system') applyTheme('system', systemDark) }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme, systemDark])

  const value = useMemo(
    () => ({ theme, setTheme: updateTheme, systemDark, setSystemDark: updateSystemDark }),
    [theme, updateTheme, systemDark, updateSystemDark],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
